import * as React from "react";
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
} from "@mui/material";
import { createTheme, alpha } from "@mui/material/styles";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";

import FileCard from "./FileCard";
import { PreviewKind } from "./FileCard/FileCard";
import CoupleGallery from "./CoupleGallery";

const CARD_W = 260;

type Queued = { id: string; file: File; preview?: string };

type UploadApiResponse = {
  result: number;
  message?: string;
  upload?: {
    id: string;
    guestName: string;
    originalName: string;
    mimeType: string;
    fileSize: number;
    uploadedAt: string;
  };
};

const UPLOAD_CONCURRENCY = 3;

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#C7A34A",
      light: "#E2C777",
      dark: "#8F6E20",
      contrastText: "#0B0B0C",
    },
    secondary: {
      main: "#F2E7C4",
      light: "#FFF8E7",
      dark: "#B8A878",
      contrastText: "#0B0B0C",
    },
    background: { default: "#09090A", paper: "#141415" },
    divider: "rgba(199,163,74,0.24)",
    text: { primary: "#F4F1E8", secondary: "#B7B1A3" },
    success: { main: "#77A981" },
    warning: { main: "#C7A34A" },
    error: { main: "#C86B6B" },
    info: { main: "#8CA4CF" },
  },
  shape: { borderRadius: 5 },
  typography: {
    fontFamily:
      '"Source Sans 3",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 500,
      letterSpacing: ".12em",
      lineHeight: 1,
      fontSize: "clamp(4.2rem, 13vw, 5.5rem)",
    },
    h2: {
      fontFamily: '"Source Sans 3", sans-serif',
      fontWeight: 300,
      textTransform: "uppercase",
      fontSize: "clamp(0.7rem, 2.5vw, 1rem)",
      letterSpacing: "clamp(.24em, 1vw, .45em)",
    },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: "#09090A",
        },
        body: {
          backgroundColor: "#09090A",
          backgroundImage: `
            radial-gradient(900px 500px at 10% -10%, rgba(199,163,74,0.10), transparent 62%),
            radial-gradient(700px 420px at 100% 20%, rgba(199,163,74,0.06), transparent 58%),
            linear-gradient(rgba(255,255,255,0.014) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.014) 1px, transparent 1px)
          `,
          backgroundSize: "auto, auto, 32px 32px, 32px 32px",
          minHeight: "100vh",
          paddingBottom: "env(safe-area-inset-bottom)",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 3,
          paddingInline: 18,
          transition: "transform .06s ease, box-shadow .2s ease",
          "&:active": { transform: "translateY(1px)" },
          "&.Mui-disabled": {
            opacity: 0.55,
            border: "1px dashed rgba(199,163,74,.22)", // ⬅️ disabled nadal widoczny
          },
        },
        containedPrimary: {
          background: "linear-gradient(180deg, #D8BC69 0%, #A98325 100%)",
          color: "#0B0B0C",
          boxShadow: "0 10px 28px rgba(199,163,74,0.18)",
          "&:hover": {
            background: "linear-gradient(180deg, #E7CE7D 0%, #B9912E 100%)",
            boxShadow: "0 12px 32px rgba(199,163,74,0.25)",
          },
        },
        outlined: {
          borderColor: "rgba(199,163,74,0.42)",
          "&:hover": {
            borderColor: "rgba(226,199,119,0.78)",
            backgroundColor: "rgba(199,163,74,0.06)",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "::placeholder": {
            color: "rgba(244,241,232,.55)", // ⬅️ mocniejszy placeholder
            opacity: 1,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 3 },
        outlined: { borderColor: "rgba(199,163,74,0.38)" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          border: "1px solid rgba(199,163,74,0.22)",
          boxShadow: "0 16px 36px rgba(0,0,0,0.24)",
          background:
            "linear-gradient(180deg, rgba(199,163,74,0.05), rgba(20,20,21,0.96) 34%)",
        },
      },
    },
    MuiDialog: {
      defaultProps: { fullWidth: true, maxWidth: "sm" },
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 6,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          boxShadow: "0 24px 60px rgba(0,0,0,.18)",
          backgroundImage: `
            radial-gradient(900px 420px at -10% -10%, ${alpha(theme.palette.primary.main, 0.1)}, transparent 62%),
            linear-gradient(180deg, #171718 0%, #101011 100%)
          `,
          backgroundRepeat: "no-repeat",
          backgroundSize: "auto",
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0,0,0,.72)",
          backdropFilter: "blur(5px)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: 8,
        },
      },
    },
    MuiDialogContent: { styleOverrides: { root: { paddingTop: 12 } } },
    MuiDialogActions: { styleOverrides: { root: { padding: 16 } } },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 2, height: 8, backgroundColor: "#242425" },
        bar: { borderRadius: 2, backgroundColor: "#C7A34A" },
      },
    },
  },
});

function UploadPage() {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [guestName, setGuestName] = React.useState("");
  const [queue, setQueue] = React.useState<Queued[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [uploadError, setUploadError] = React.useState("");

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [uploadPhase, setUploadPhase] = React.useState<
    "idle" | "uploading" | "success" | "error"
  >("idle");

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewSrc, setPreviewSrc] = React.useState<string | undefined>();
  const [previewName, setPreviewName] = React.useState<string | undefined>();
  const [previewKind, setPreviewKind] = React.useState<
    PreviewKind | undefined
  >();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileView, setMobileView] = React.useState<"grid" | "list">("list");

  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  const sig = (f: File) => `${f.name}::${f.size}::${f.lastModified}`;

  const onChoose = (files: FileList | null) => {
    if (!files?.length) return;
    const selectedFiles = Array.from(files);

    setQueue((prev) => {
      const bySig = new Map(prev.map((q) => [sig(q.file), q]));
      const seenNow = new Set<string>();
      const added: Queued[] = [];
      const bumpedOrder: string[] = [];

      for (const f of selectedFiles) {
        const s = sig(f);
        if (seenNow.has(s)) continue;
        seenNow.add(s);

        if (bySig.has(s)) {
          if (!bumpedOrder.includes(s)) bumpedOrder.push(s);
        } else {
          added.push({
            id: crypto.randomUUID(),
            file: f,
            preview: /^image\/|^video\//.test(f.type)
              ? URL.createObjectURL(f)
              : undefined,
          });
        }
      }

      const bumpedSet = new Set(bumpedOrder);
      const rest = prev.filter((q) => !bumpedSet.has(sig(q.file)));
      const bumpedItems = bumpedOrder.map((s) => bySig.get(s)!);
      return [...rest, ...added, ...bumpedItems];
    });

    if (inputRef.current) inputRef.current.value = "";
  };

  const removeOne = (id: string) => {
    setQueue((q) => {
      const item = q.find((x) => x.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return q.filter((x) => x.id !== id);
    });
    setSelected((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const clearAll = () => {
    queue.forEach((q) => q.preview && URL.revokeObjectURL(q.preview));
    setQueue([]);
    setSelected(new Set());
  };

  React.useEffect(() => {
    return () => {
      queue.forEach((q) => q.preview && URL.revokeObjectURL(q.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadSingleFile = async (
    file: File,
    normalizedGuestName: string,
  ): Promise<UploadApiResponse> => {
    const fileType = file.type || "application/octet-stream";

    const urlResponse = await fetch("/api/upload-url", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType,
      }),
    });

    const urlData = (await urlResponse.json()) as {
      result: number;
      message?: string;
      upload?: {
        uploadId: string;
        r2Key: string;
        uploadUrl: string;
        headers: Record<string, string>;
      };
    };

    if (!urlResponse.ok || urlData.result !== 0 || !urlData.upload) {
      throw new Error(
        urlData.message ||
          `Nie udało się przygotować wysyłania pliku „${file.name}”.`,
      );
    }

    const directUploadResponse = await fetch(urlData.upload.uploadUrl, {
      method: "PUT",
      headers: urlData.upload.headers,
      body: file,
    });

    if (!directUploadResponse.ok) {
      throw new Error(
        `Nie udało się wysłać pliku „${file.name}” do magazynu.`,
      );
    }

    const completeResponse = await fetch("/api/upload-complete", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        uploadId: urlData.upload.uploadId,
        r2Key: urlData.upload.r2Key,
        guestName: normalizedGuestName,
        originalName: file.name,
        mimeType: fileType,
      }),
    });

    let completeData: UploadApiResponse;

    try {
      completeData =
        (await completeResponse.json()) as UploadApiResponse;
    } catch {
      throw new Error(
        `Plik „${file.name}” został wysłany, ale serwer zwrócił nieprawidłową odpowiedź.`,
      );
    }

    if (!completeResponse.ok || completeData.result !== 0) {
      throw new Error(
        completeData.message ||
          `Plik „${file.name}” został wysłany, ale nie pojawił się w galerii.`,
      );
    }

    return completeData;
  };

  const onUpload = async () => {
    const normalizedGuestName = guestName.trim() || "Tajemniczy Gość";

    if (!queue.length || busy) return;

    const itemsToUpload = queue.slice();
    const failedFiles: string[] = [];
    const successfulIds = new Set<string>();
    let nextIndex = 0;
    let finishedCount = 0;

    setDialogOpen(true);
    setUploadPhase("uploading");
    setUploadError("");
    setBusy(true);
    setProgress(0);

    const worker = async () => {
      while (true) {
        const currentIndex = nextIndex;
        nextIndex += 1;

        if (currentIndex >= itemsToUpload.length) {
          return;
        }

        const item = itemsToUpload[currentIndex];

        try {
          await uploadSingleFile(item.file, normalizedGuestName);
          successfulIds.add(item.id);
        } catch (error) {
          console.error(`Upload failed for ${item.file.name}:`, error);
          failedFiles.push(item.file.name);
        } finally {
          finishedCount += 1;
          setProgress(
            Math.round((finishedCount / itemsToUpload.length) * 100),
          );
        }
      }
    };

    try {
      const workersCount = Math.min(
        UPLOAD_CONCURRENCY,
        itemsToUpload.length,
      );

      await Promise.all(
        Array.from({ length: workersCount }, () => worker()),
      );

      if (failedFiles.length > 0) {
        const visibleNames = failedFiles.slice(0, 4).join(", ");
        const remainingCount = failedFiles.length - 4;

        setQueue((currentQueue) => {
          currentQueue.forEach((item) => {
            if (successfulIds.has(item.id) && item.preview) {
              URL.revokeObjectURL(item.preview);
            }
          });

          return currentQueue.filter((item) => !successfulIds.has(item.id));
        });

        setSelected((currentSelected) => {
          const nextSelected = new Set(currentSelected);
          successfulIds.forEach((id) => nextSelected.delete(id));
          return nextSelected;
        });

        setUploadError(
          remainingCount > 0
            ? `Nie udało się wysłać: ${visibleNames} i jeszcze ${remainingCount} plików. Spróbuj ponownie.`
            : `Nie udało się wysłać: ${visibleNames}. Spróbuj ponownie.`,
        );
        setUploadPhase("error");
        return;
      }

      setProgress(100);
      setUploadPhase("success");
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError(
        error instanceof Error
          ? error.message
          : "Nie udało się przesłać plików. Spróbuj ponownie.",
      );
      setUploadPhase("error");
    } finally {
      setBusy(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (uploadPhase === "success") {
      clearAll();
    }
    setUploadPhase("idle");
    setUploadError("");
    setProgress(0);
  };

  const handleDialogRequestClose = (
    _e: object,
    _reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (uploadPhase === "uploading") return;
    handleDialogClose();
  };

  const handlePreview = (src: string, name: string, kind: PreviewKind) => {
    setPreviewSrc(src);
    setPreviewName(name);
    setPreviewKind(kind);
    setPreviewOpen(true);
  };

  const toggleSelectOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(queue.map((q) => q.id)));
  const clearSelection = () => setSelected(new Set());
  const removeSelected = () => {
    if (!selected.size) return;
    setQueue((q) => {
      q.forEach((item) => {
        if (selected.has(item.id) && item.preview)
          URL.revokeObjectURL(item.preview);
      });
      return q.filter((item) => !selected.has(item.id));
    });
    setSelected(new Set());
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container
        maxWidth="md"
        sx={{
          py: 5,
          pb: isMobile ? "calc(96px + env(safe-area-inset-bottom))" : 5,
        }}
      >
        <Stack spacing={2.5}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 3, sm: 6 },
              py: { xs: 5.5, sm: 7.5 },
              color: "text.primary",
              background: `
                radial-gradient(circle at 50% 12%, rgba(199,163,74,.14), transparent 36%),
                linear-gradient(145deg, rgba(28,28,29,.99), rgba(7,7,8,.995))
              `,
              border: "1px solid rgba(199,163,74,.36)",
              borderRadius: 1.5,
              boxShadow:
                "0 24px 70px rgba(0,0,0,.38), inset 0 0 0 1px rgba(255,255,255,.02)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 12,
                border: "1px solid rgba(199,163,74,.16)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                width: 420,
                height: 420,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(199,163,74,.08), transparent 70%)",
                top: -250,
                right: -110,
                pointerEvents: "none",
              },
            }}
          >
            <Stack
              spacing={2.2}
              alignItems="center"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "primary.light",
                  letterSpacing: ".34em",
                  fontSize: ".68rem",
                }}
              >
                NASZ WYJĄTKOWY DZIEŃ
              </Typography>

              <Box
                aria-label="Inicjały pary młodej: W i D"
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: { xs: 210, sm: 280 },
                  py: 0.5,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontWeight: 500,
                    fontSize: "clamp(5.4rem, 18vw, 8.2rem)",
                    lineHeight: 0.8,
                    letterSpacing: "-.03em",
                    background:
                      "linear-gradient(180deg, #FFF4BF 0%, #E4C86F 26%, #C49A35 58%, #806018 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    color: "transparent",
                    filter: "drop-shadow(0 8px 18px rgba(199,163,74,.13))",
                  }}
                >
                  W
                </Typography>

                <Typography
                  component="span"
                  aria-hidden="true"
                  sx={{
                    mx: { xs: 1.2, sm: 1.8 },
                    mt: 1,
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: "clamp(2rem, 6vw, 3rem)",
                    fontStyle: "italic",
                    fontWeight: 400,
                    lineHeight: 1,
                    color: "primary.light",
                    textShadow: "0 0 18px rgba(199,163,74,.20)",
                  }}
                >
                  &amp;
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontWeight: 500,
                    fontSize: "clamp(5.4rem, 18vw, 8.2rem)",
                    lineHeight: 0.8,
                    letterSpacing: "-.03em",
                    background:
                      "linear-gradient(180deg, #FFF4BF 0%, #E4C86F 26%, #C49A35 58%, #806018 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    color: "transparent",
                    filter: "drop-shadow(0 8px 18px rgba(199,163,74,.13))",
                  }}
                >
                  D
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  width: "min(320px, 80%)",
                }}
              >
                <Box
                  sx={{
                    height: "1px",
                    flex: 1,
                    bgcolor: "rgba(199,163,74,.45)",
                  }}
                />
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    border: "1px solid",
                    borderColor: "primary.main",
                    transform: "rotate(45deg)",
                  }}
                />
                <Box
                  sx={{
                    height: "1px",
                    flex: 1,
                    bgcolor: "rgba(199,163,74,.45)",
                  }}
                />
              </Box>

              <Typography variant="h2">Wrzutka weselna</Typography>

              <Stack
                spacing={1.8}
                alignItems="center"
                sx={{ maxWidth: 620, mx: "auto" }}
              >
                <Typography
                  component="p"
                  sx={{
                    m: 0,
                    px: { xs: 0.5, sm: 2 },
                    textAlign: "center",
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: { xs: "1.45rem", sm: "1.75rem" },
                    fontWeight: 500,
                    lineHeight: 1.28,
                    color: "text.primary",
                  }}
                >
                  Podzielcie się z nami wspomnieniami
                  <br />
                  z tego wyjątkowego dnia.
                </Typography>

                <Box
                  aria-hidden="true"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    width: "min(190px, 55vw)",
                  }}
                >
                  <Box
                    sx={{
                      height: "1px",
                      flex: 1,
                      background:
                        "linear-gradient(90deg, transparent, rgba(199,163,74,.7))",
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      color: "primary.light",
                      fontSize: ".72rem",
                      lineHeight: 1,
                      textShadow: "0 0 12px rgba(199,163,74,.3)",
                    }}
                  >
                    ✦
                  </Typography>
                  <Box
                    sx={{
                      height: "1px",
                      flex: 1,
                      background:
                        "linear-gradient(90deg, rgba(199,163,74,.7), transparent)",
                    }}
                  />
                </Box>

                <Typography
                  component="p"
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    m: 0,
                    maxWidth: 570,
                    px: { xs: 0.5, sm: 2 },
                    textAlign: "center",
                    lineHeight: 1.8,
                    fontSize: { xs: ".96rem", sm: "1.04rem" },
                  }}
                >
                  Dodajcie zdjęcia i filmy uchwycone telefonem, abyśmy mogli
                  zachować wszystkie piękne chwile i wracać do nich przez
                  długie lata.
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              fullWidth
              label="Podpis gościa"
              placeholder="np. Anna i Piotr"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              sx={{
                "& > div > fieldset": { borderColor: "primary.light" },
                ...(guestName
                  ? { "& > label": { color: "primary.main" } }
                  : {}),
              }}
            />
            {!isMobile && (
              <>
                <Tooltip title="Dodaj">
                  <span>
                    <Button
                      onClick={() => inputRef.current?.click()}
                      variant="contained"
                      startIcon={<AddPhotoAlternateIcon />}
                      disabled={busy}
                    >
                      Dodaj pliki
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Wyślij Młodym">
                  <span>
                    <Button
                      onClick={onUpload}
                      variant="contained"
                      color="primary"
                      endIcon={<SendIcon />}
                      disabled={!queue.length || busy}
                    >
                      Wyślij Młodym
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>

          {queue.length > 0 && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Chip
                variant="outlined"
                color={
                  queue.length
                    ? selected.size
                      ? "primary"
                      : "default"
                    : "default"
                }
                label={`${selected.size} zaznaczonych`}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={selectAll}
                  disabled={!queue.length || selected.size === queue.length}
                >
                  Zaznacz wszystko
                </Button>
                <Button
                  size="small"
                  onClick={clearSelection}
                  disabled={!selected.size}
                >
                  Odznacz wszystko
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={removeSelected}
                  disabled={!selected.size}
                >
                  Usuń zaznaczone
                </Button>
              </Stack>

              {isMobile && (
                <Box sx={{ ml: { sm: "auto" } }}>
                  <ToggleButtonGroup
                    size="small"
                    fullWidth
                    exclusive
                    value={mobileView}
                    onChange={(_, v) => v && setMobileView(v)}
                    color="primary"
                    sx={{ borderColor: "primary.light" }}
                  >
                    <ToggleButton
                      sx={{ borderColor: "primary.light" }}
                      fullWidth
                      value="grid"
                    >
                      Kafelki
                    </ToggleButton>
                    <ToggleButton
                      sx={{ borderColor: "primary.light" }}
                      fullWidth
                      value="list"
                    >
                      Lista
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Stack>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            hidden
            onChange={(e) => onChoose(e.target.files)}
          />

          {queue.length > 0 &&
            (isMobile && mobileView === "list" ? (
              <Box>
                {queue.map((q) => {
                  const isImage = q.file.type.startsWith("image/");
                  const isVideo = q.file.type.startsWith("video/");
                  return (
                    <Box
                      key={q.id}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleSelectOne(q.id);
                      }}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "64px 1fr auto",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        border: "1px solid",
                        borderColor: "primary.light",
                        borderRadius: 1.5,
                        mb: 1,
                        bgcolor: "transparent",
                        outline: selected.has(q.id) ? "2px solid" : "none",
                        outlineColor: selected.has(q.id)
                          ? "primary.main"
                          : "transparent",
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 1,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "background.default",
                          cursor:
                            (isImage || isVideo) && q.preview
                              ? "pointer"
                              : "default",
                          pointerEvents: "none",
                        }}
                      >
                        {isImage && q.preview ? (
                          <img
                            src={q.preview}
                            alt={q.file.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : isVideo && q.preview ? (
                          <video
                            src={q.preview}
                            muted
                            playsInline
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <InsertDriveFileIcon />
                        )}
                      </Box>

                      <Box sx={{ overflow: "hidden" }}>
                        <Typography variant="body2" noWrap title={q.file.name}>
                          {q.file.name}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Usuń">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeOne(q.id);
                              }}
                              disabled={busy}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(auto-fill, minmax(160px, 1fr))",
                    sm: `repeat(auto-fill, minmax(${CARD_W}px, 1fr))`,
                  },
                  gap: { xs: 1.5, sm: 2 },
                  justifyItems: "stretch",
                  alignItems: "stretch",
                }}
              >
                {queue.map((q) => (
                  <Box key={q.id} sx={{ width: "100%" }}>
                    <FileCard
                      id={q.id}
                      file={q.file}
                      preview={q.preview}
                      busy={busy}
                      onRemove={removeOne}
                      onPreview={handlePreview}
                      selected={selected.has(q.id)}
                      onToggleSelect={toggleSelectOne}
                      isMobile={isMobile}
                    />
                  </Box>
                ))}
              </Box>
            ))}
        </Stack>
      </Container>

      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: "calc(8px + env(safe-area-inset-bottom))",
            zIndex: (t) => t.zIndex.appBar,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              background:
                "linear-gradient(145deg, rgba(25,25,26,.96), rgba(10,10,11,.96))",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
              display: "flex",
              gap: 2,
              backdropFilter: "blur(5px)",
            }}
          >
            <Button
              onClick={() => inputRef.current?.click()}
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<AddPhotoAlternateIcon />}
              disabled={busy}
              sx={{ backgroundColor: "rgba(15,15,16,.88)" }}
            >
              Dodaj
            </Button>
            <Button
              onClick={onUpload}
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              endIcon={<SendIcon />}
              disabled={!queue.length || busy}
            >
              Wyślij
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleDialogRequestClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {uploadPhase === "success" && (
            <CheckCircleOutlineIcon color="success" />
          )}
          {uploadPhase === "uploading"
            ? "Wysyłanie plików"
            : uploadPhase === "success"
              ? "Wysłano"
              : uploadPhase === "error"
                ? "Nie udało się wysłać"
                : "Wysyłanie plików"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack
            spacing={2}
            sx={{
              ...(["success", "uploading", "error"].includes(uploadPhase)
                ? {
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                  }
                : {}),
            }}
          >
            {uploadPhase === "uploading" && (
              <Stack
                spacing={2.4}
                alignItems="center"
                sx={{
                  width: "100%",
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 126,
                    height: 126,
                    display: "grid",
                    placeItems: "center",
                    "@keyframes uploadGlow": {
                      "0%, 100%": {
                        boxShadow:
                          "0 0 0 0 rgba(199,163,74,.18), 0 0 28px rgba(199,163,74,.18)",
                      },
                      "50%": {
                        boxShadow:
                          "0 0 0 12px rgba(199,163,74,0), 0 0 48px rgba(226,199,119,.42)",
                      },
                    },
                    "@keyframes uploadSpin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                    borderRadius: "50%",
                    animation: "uploadGlow 1.8s ease-in-out infinite",
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      inset: 3,
                      borderRadius: "50%",
                      background:
                        "conic-gradient(from 0deg, transparent 0 62%, rgba(226,199,119,.85) 76%, transparent 92%)",
                      animation: "uploadSpin 1.25s linear infinite",
                      filter: "blur(.2px)",
                      opacity: 0.9,
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      inset: 10,
                      borderRadius: "50%",
                      bgcolor: "background.paper",
                      boxShadow:
                        "inset 0 0 0 1px rgba(199,163,74,.22), inset 0 0 22px rgba(199,163,74,.05)",
                    }}
                  />

                  <CircularProgress
                    variant="determinate"
                    size={106}
                    thickness={3.5}
                    value={progress}
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      color: "primary.main",
                      "& .MuiCircularProgress-circle": {
                        strokeLinecap: "round",
                        filter:
                          "drop-shadow(0 0 5px rgba(226,199,119,.45))",
                      },
                    }}
                  />

                  <Typography
                    component="div"
                    sx={{
                      position: "absolute",
                      zIndex: 2,
                      fontWeight: 600,
                      color: "primary.light",
                      fontFamily:
                        '"Cormorant Garamond","Times New Roman",serif',
                      fontSize: "1.55rem",
                      textShadow: "0 0 14px rgba(199,163,74,.28)",
                    }}
                  >
                    {`${Math.round(progress)}%`}
                  </Typography>
                </Box>

                <Stack spacing={0.7} alignItems="center">
                  <Typography
                    sx={{
                      fontFamily:
                        '"Cormorant Garamond","Times New Roman",serif',
                      fontSize: "1.45rem",
                      color: "text.primary",
                    }}
                  >
                    Wysyłamy Twoje wspomnienia
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", maxWidth: 360 }}
                  >
                    Duże filmy mogą potrzebować trochę więcej czasu. Nie zamykaj
                    tej strony, dopóki wysyłanie się nie zakończy.
                  </Typography>

                  <Box
                    aria-label="Wysyłanie w toku"
                    sx={{
                      display: "flex",
                      gap: 0.8,
                      mt: 0.5,
                      "@keyframes uploadDot": {
                        "0%, 80%, 100%": {
                          opacity: 0.28,
                          transform: "translateY(0)",
                        },
                        "40%": {
                          opacity: 1,
                          transform: "translateY(-4px)",
                        },
                      },
                    }}
                  >
                    {[0, 1, 2].map((dot) => (
                      <Box
                        key={dot}
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "primary.light",
                          animation: "uploadDot 1.2s ease-in-out infinite",
                          animationDelay: `${dot * 0.16}s`,
                          boxShadow:
                            "0 0 10px rgba(226,199,119,.45)",
                        }}
                      />
                    ))}
                  </Box>
                </Stack>
              </Stack>
            )}
            {uploadPhase === "success" && (
              <Alert
                severity="success"
                icon={<></>}
                sx={{
                  backgroundColor: "transparent",
                  fontFamily: '"Cormorant Garamond","Times New Roman",serif',
                  fontSize: "2rem",
                  display: "flex",
                  justifyContent: "center",
                  "& > div:first-of-type": { display: "none" },
                }}
              >
                Dziękujemy za wrzutkę!
              </Alert>
            )}
            {uploadPhase === "error" && (
              <Alert
                severity="error"
                sx={{
                  width: "100%",
                  maxWidth: 520,
                  border: "1px solid",
                  borderColor: "rgba(200,107,107,.35)",
                  backgroundColor: "rgba(200,107,107,.08)",
                  lineHeight: 1.6,
                }}
              >
                {uploadError ||
                  "Nie udało się przesłać plików. Spróbuj ponownie."}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          {uploadPhase === "uploading" ? (
            <Button disabled>Wysyłanie…</Button>
          ) : (
            <Button onClick={handleDialogClose} variant="contained">
              Zamknij
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography
            component="span"
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 700, color: "text.primary" }}
            title={previewName || "Podgląd"}
          >
            {previewName || "Podgląd"}
          </Typography>

          <IconButton
            aria-label="Zamknij podgląd"
            onClick={() => setPreviewOpen(false)}
            size="medium"
            sx={(t) => ({
              position: "absolute",
              right: 12,
              top: 12,
              width: 36,
              height: 36,
              color: "#C7A34A",
              "&:hover": {
                backgroundColor: alpha(t.palette.primary.main, 0.16),
              },
            })}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, display: "flex", justifyContent: "center" }}
        >
          {previewSrc && previewKind === "video" && (
            <video
              key={previewSrc}
              src={previewSrc}
              controls
              autoPlay
              playsInline
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                background: "#050505",
              }}
            />
          )}
          {previewSrc && previewKind === "image" && (
            <img
              key={previewSrc}
              src={previewSrc}
              alt={previewName}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                display: "block",
                background: "#050505",
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}


export default function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";

  if (path === "/dla-pary") {
    return <CoupleGallery />;
  }

  return <UploadPage />;
}
